class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', async (event) => {
      event.preventDefault();
      cartLink.setAttribute('aria-busy', 'true');
      try {
        await this.refreshFromServer();
      } catch (error) {
        console.error('Cart drawer refresh failed.', error);
      } finally {
        cartLink.removeAttribute('aria-busy');
      }
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async refreshFromServer() {
    if (this.refreshRequest) return this.refreshRequest;

    const sectionIds = this.getSectionsToRender().map((section) => section.id).join(',');
    const cartUrl = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : '/cart';

    this.refreshRequest = fetch(`${cartUrl}?sections=${encodeURIComponent(sectionIds)}&_bp=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart refresh failed with status ${response.status}`);
        return response.json();
      })
      .then((sections) => this.renderContents({ sections }, false))
      .finally(() => {
        this.refreshRequest = null;
      });

    return this.refreshRequest;
  }

  renderContents(parsedState, shouldOpen = true) {
    const drawerHtml = parsedState.sections?.['cart-drawer'];
    const renderedDrawer = drawerHtml ? this.getSectionDOM(drawerHtml, 'cart-drawer') : null;

    if (renderedDrawer) {
      this.classList.toggle('is-empty', renderedDrawer.classList.contains('is-empty'));
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHtml) return;
      const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHTML === null) return;
      sectionElement.innerHTML = innerHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (shouldOpen) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// Inline cart accessories: filter groups, calculate requirements, and add without navigation.
(() => {
  let addingAccessory = false;
  const ceilPacks = value => Math.max(1, Math.ceil(value - 1e-9));
  function requirement(card) {
    const d = card.dataset;
    const area = Number(d.area) * (1 + Number(d.extra || 0) / 100);
    if (d.calc === 'length') {
      const metres = Number(card.querySelector('[data-bp-cart-skirting-length]')?.value);
      return metres > 0 && Number(d.lengthMm) > 0 ? ceilPacks(metres / (Number(d.lengthMm) / 1000)) : 0;
    }
    if (d.calc === 'einzeln') return 1;
    if (d.calc === 'abdeckung') return Number(d.coverage) > 0 ? ceilPacks(area / Number(d.coverage)) : 0;
    if (d.calc === 'flaeche') return Number(d.content) > 0 && Number(d.consumption) > 0 ? ceilPacks(area * Number(d.consumption) / Number(d.content)) : 0;
    if (d.calc === 'schichtdicke') {
      const mm = Number(card.querySelector('[data-bp-cart-thickness]')?.value);
      return mm > 0 && Number(d.content) > 0 && Number(d.consumptionMm) > 0 ? ceilPacks(area * mm * Number(d.consumptionMm) / Number(d.content)) : 0;
    }
    return 0;
  }
  function updateCard(card) {
    const recommended = requirement(card);
    const quantity = card.querySelector('[data-bp-cart-accessory-quantity]');
    if (quantity.dataset.manual !== 'true') quantity.value = String(recommended || 1);
    const ordered = Math.max(1, Math.ceil(Number(quantity.value) || 1));
    card.querySelector('[data-bp-cart-requirement]').textContent = recommended ? `${recommended} ${card.dataset.unit}` : '–';
    const root = card.closest('[data-bp-cart-accessories]');
    const money = new Intl.NumberFormat(root.dataset.locale || 'de', {style:'currency',currency:root.dataset.currency || 'EUR'});
    card.querySelector('[data-bp-cart-line-total]').textContent = recommended ? money.format(ordered * Number(card.dataset.price) / 100) : '–';
    card.querySelector('[data-bp-cart-accessory-add]').disabled = !recommended || addingAccessory;
    card.dataset.recommended = String(recommended);
  }
  function filter(root) {
    const active = root.dataset.activeGroup || root.dataset.defaultGroup;
    root.querySelectorAll('[data-bp-cart-group]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bpCartGroup === active)));
    const choice = root.querySelector('[data-bp-cart-prep-choice]')?.value || '';
    const prep = root.querySelector('[data-bp-cart-preparation]');
    if (prep) {
      prep.hidden = active !== 'preparation';
      prep.querySelector('[data-bp-cart-prep-advice]').hidden = choice !== 'unsure';
      prep.querySelector('[data-bp-cart-prep-note]').hidden = choice !== 'needed';
      prep.querySelector('[data-bp-cart-prep-ready]').hidden = choice !== 'ready';
    }
    root.querySelectorAll('[data-bp-cart-accessory]').forEach(card => {
      card.hidden = card.dataset.group !== active || (active === 'preparation' && choice !== 'needed');
      updateCard(card);
    });
  }
  function initialize() {
    document.querySelectorAll('[data-bp-cart-accessories]:not([data-ready])').forEach(root => {
      root.dataset.ready = 'true';
      filter(root);
    });
  }
  const drawerNode = document.querySelector('cart-drawer');
  if (drawerNode) new MutationObserver(initialize).observe(drawerNode, {childList:true,subtree:true});
  initialize();
  document.addEventListener('input', event => {
    const card = event.target.closest('[data-bp-cart-accessory]');
    if (!card) return;
    if (event.target.hasAttribute('data-bp-cart-accessory-quantity')) event.target.dataset.manual = 'true';
    updateCard(card);
  });
  document.addEventListener('change', event => {
    if (event.target.hasAttribute('data-bp-cart-skirting-variant')) {
      const card = event.target.closest('[data-bp-cart-accessory]');
      const option = event.target.selectedOptions[0];
      card.dataset.variantId = option.value;
      card.dataset.price = option.dataset.price;
      card.dataset.lengthMm = option.dataset.lengthMm;
      card.querySelector('[data-bp-cart-accessory-quantity]').dataset.manual = 'false';
      const root = card.closest('[data-bp-cart-accessories]');
      const money = new Intl.NumberFormat(root.dataset.locale || 'de', {style:'currency',currency:root.dataset.currency || 'EUR'});
      card.querySelector('[data-bp-cart-skirting-price]').textContent = `${money.format(Number(option.dataset.price) / 100)} / ${card.dataset.unitSingular}`;
      const image = card.querySelector('img');
      if (image && option.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
      updateCard(card);
    }
    if (event.target.hasAttribute('data-bp-cart-prep-choice')) filter(event.target.closest('[data-bp-cart-accessories]'));
  });
  document.addEventListener('click', async event => {
    const groupButton = event.target.closest('[data-bp-cart-group]');
    if (groupButton) {
      const root = groupButton.closest('[data-bp-cart-accessories]');
      root.dataset.activeGroup = groupButton.dataset.bpCartGroup;
      filter(root);
      return;
    }
    const button = event.target.closest('[data-bp-cart-accessory-add]');
    if (!button || addingAccessory || button.disabled) return;
    const card = button.closest('[data-bp-cart-accessory]');
    const root = card.closest('[data-bp-cart-accessories]');
    if (card.dataset.group === 'preparation' && root.querySelector('[data-bp-cart-prep-choice]')?.value !== 'needed') return;
    const quantity = Math.max(1, Math.ceil(Number(card.querySelector('[data-bp-cart-accessory-quantity]').value) || 1));
    const drawer = button.closest('cart-drawer');
    const message = card.querySelector('[data-bp-cart-accessory-message]');
    const properties = {'Passend zum Boden':card.dataset.floorTitle};
    if (card.dataset.calc === 'length') properties.Wandlänge = `${card.querySelector('[data-bp-cart-skirting-length]').value} lfm`;
    if (card.dataset.calc === 'schichtdicke') properties.Schichtdicke = `${card.querySelector('[data-bp-cart-thickness]').value} mm`;
    addingAccessory = true;
    root.querySelectorAll('[data-bp-cart-accessory]').forEach(updateCard);
    message.textContent = '';
    drawer.setActiveElement(button);
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({items:[{id:Number(card.dataset.variantId),quantity,properties}],sections:'cart-drawer,cart-icon-bubble',sections_url:location.pathname})
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || card.dataset.error);
      if (result.sections?.['cart-drawer'] && result.sections?.['cart-icon-bubble']) drawer.renderContents(result,true);
      else { await drawer.refreshFromServer(); drawer.open(); }
      const replacement = drawer.querySelector('[data-bp-cart-accessories]');
      if (replacement) {
        replacement.open = true;
        if (replacement.querySelector(`[data-bp-cart-group="${root.dataset.activeGroup || root.dataset.defaultGroup}"]`)) replacement.dataset.activeGroup = root.dataset.activeGroup || root.dataset.defaultGroup;
        const replacementChoice = replacement.querySelector('[data-bp-cart-prep-choice]');
        if (replacementChoice) replacementChoice.value = root.querySelector('[data-bp-cart-prep-choice]')?.value || '';
      }
    } catch (error) {
      message.textContent = error.message || card.dataset.error;
    } finally {
      addingAccessory = false;
      initialize();
      drawer.querySelectorAll('[data-bp-cart-accessories]').forEach(filter);
    }
  });
})();

